/**
 * A Team Assist V2 answer as the panel renders it, built from glados's turn
 * stream (`reduceTeamAssistV2Event`) or from a stored answer
 * (`answerFromStoredMessage`). Plain objects, never mutated.
 *
 * `blocks` are in the order the model wrote them, each carrying the `index`
 * glados gave it: `text` and `suggested_reply` hold `text`, and `tool_use`
 * holds the call (`toolUseId`, `name`, `input`) and, when its result surfaced
 * as a card, `execution` (`result`, `resultType`, `isError`). `rating` is the
 * agent's thumbs on a finished answer, in V1's shape (`{ value, reason?,
 * comment? }`, see `TeamAssistRating`), or null.
 */

export const TeamAssistV2AnswerStatus = {
  streaming: 'streaming',
  done: 'done',
  error: 'error',
};

// Why an answer failed. Only `server` carries a message, glados's own.
export const TeamAssistV2ErrorCode = {
  server: 'server',
  requestFailed: 'request_failed',
  network: 'network',
  interrupted: 'interrupted',
};

export function newTeamAssistV2Answer() {
  return {
    status: TeamAssistV2AnswerStatus.streaming,
    blocks: [],
    thinking: '',
    citations: [],
    tags: [],
    questionId: null,
    messageId: null,
    error: null,
    rating: null,
  };
}

/**
 * Folds one stream event into the answer. Text stays provisional until `done`:
 * `blocks_retracted` drops the blocks the model took back, and `error` drops
 * everything the turn streamed. An event this client doesn't know leaves the
 * answer as it was.
 */
export function reduceTeamAssistV2Event(answer, event) {
  switch (event.type) {
    case 'block_open':
      return {
        ...answer,
        blocks: upsertBlock(answer.blocks, { index: event.index, type: event.blockType, text: '', complete: false }),
      };

    case 'block_delta':
      return {
        ...answer,
        blocks: mapBlock(answer.blocks, event.index, block => ({ ...block, text: block.text + (event.delta || '') })),
      };

    case 'block_close':
      return { ...answer, blocks: mapBlock(answer.blocks, event.index, block => ({ ...block, complete: true })) };

    case 'block_full':
      return { ...answer, blocks: upsertBlock(answer.blocks, fullBlock(event.index, event.block || {})) };

    case 'blocks_retracted':
      return { ...answer, blocks: answer.blocks.filter(block => block.index < event.fromIndex) };

    case 'tool_executed':
      return {
        ...answer,
        blocks: answer.blocks.map(block => {
          if (block.type !== 'tool_use' || block.toolUseId !== event.toolUseId) {
            return block;
          }
          return { ...block, execution: execution(event) };
        }),
      };

    case 'thinking_delta':
      return { ...answer, thinking: answer.thinking + (event.delta || '') };

    case 'done':
      return {
        ...answer,
        status: TeamAssistV2AnswerStatus.done,
        questionId: event.questionId || null,
        messageId: event.messageId || null,
        citations: event.citations || [],
        tags: event.tags || [],
      };

    case 'error':
      return {
        ...answer,
        status: TeamAssistV2AnswerStatus.error,
        blocks: [],
        thinking: '',
        questionId: event.questionId || answer.questionId,
        error: event.code
          ? { code: event.code }
          : { code: TeamAssistV2ErrorCode.server, message: event.message || null },
      };

    default:
      return answer;
  }
}

/**
 * A stored answer (a `role: 'assistant'` message from the load route) as a
 * finished answer. `executionsByToolUseId` holds the surfaced results from the
 * session's `tool_executed` messages, and `rating` the agent's rating on it.
 */
export function answerFromStoredMessage(message, executionsByToolUseId = {}, rating = null) {
  const content = message.content || [];
  return {
    ...newTeamAssistV2Answer(),
    status: TeamAssistV2AnswerStatus.done,
    blocks: content
      .map((block, index) => ({ block, index }))
      .filter(({ block }) => block.type !== 'thinking')
      .map(({ block, index }) => {
        const shaped = fullBlock(index, block);
        const stored = block.type === 'tool_use' ? executionsByToolUseId[block.toolUseId] : null;
        return stored ? { ...shaped, execution: stored } : shaped;
      }),
    thinking: content
      .filter(block => block.type === 'thinking')
      .map(block => block.text || '')
      .join(''),
    citations: message.citations || [],
    tags: message.tags || [],
    questionId: message.questionId || null,
    messageId: message.id || null,
    rating,
  };
}

function fullBlock(index, block) {
  if (block.type === 'tool_use') {
    return {
      index,
      type: 'tool_use',
      toolUseId: block.toolUseId,
      name: block.name,
      input: block.input,
      complete: true,
    };
  }
  return { index, type: block.type, text: block.text || '', complete: true };
}

function execution(event) {
  return { result: event.result, resultType: event.resultType || null, isError: event.isError === true };
}

function upsertBlock(blocks, next) {
  const existing = blocks.findIndex(block => block.index === next.index);
  if (existing === -1) {
    return [...blocks, next].sort((a, b) => a.index - b.index);
  }
  const copy = blocks.slice();
  copy[existing] = { ...copy[existing], ...next };
  return copy;
}

function mapBlock(blocks, index, fn) {
  return blocks.map(block => (block.index === index ? fn(block) : block));
}
